/**
 * Info-Symbol an einer Kennzahl: zeigt Formel und Einschränkung.
 *
 * Anlass (Armin Kern, Bereichsleiter, 08.09.2026): „Bei KPIs fände ich gut,
 * wenn irgendwo eine Beschreibung sichtbar ist, wie sie sich zusammensetzt."
 *
 * Die Darstellung gab es schon — eingebaut in die Kachel des Detailfensters
 * und damit nur dort verfügbar. Hier steht sie eigenständig, damit die
 * Übersicht und „Mein Team" dieselbe Erklärung zeigen können, statt eine
 * zweite zu erfinden. Den Text liefert `lib/glossar.ts`.
 *
 * Bedienung bewusst doppelt: Zeigen beim Überfahren (schnell für die Maus) UND
 * Umschalten beim Klick (Tastatur, Touch, und für alle, die den Text in Ruhe
 * lesen wollen, ohne die Maus stillzuhalten).
 *
 * Der Kasten liegt auf einer eigenen Ebene über der Seite (Portal), nicht in
 * der Kachel. Anlass (Marie, 16.09.2026): Unter „Ø Auslastung 3M" klappte er
 * auf und verschwand zur Hälfte hinter der Nachbarkachel „Mitarbeiter".
 * Die Lage rechnet `lib/popover-position.ts`.
 */
import { Info } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { GLOSSAR } from "../../lib/glossar.js";
import { berechnePopoverPosition } from "../../lib/popover-position.js";

export function KpiInfo({
  schluessel,
  label,
  zusatz,
}: {
  schluessel: string;
  label: string;
  /** Stufenabhängiger Teil (Anlaufziel, Listenpreis, Wochenkosten) — steht unter der festen Erklärung. */
  zusatz?: string | undefined;
}) {
  const [offen, setOffen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const symbolRef = useRef<HTMLButtonElement>(null);
  const kastenRef = useRef<HTMLSpanElement>(null);
  const eintrag = GLOSSAR[schluessel];

  // Erst unsichtbar zeichnen, dann messen und platzieren — die Höhe des
  // Kastens hängt vom Text ab und entscheidet, ob er nach oben aufklappt.
  useLayoutEffect(() => {
    if (!offen) {
      setPosition(null);
      return;
    }
    const symbol = symbolRef.current;
    const kasten = kastenRef.current;
    if (!symbol || !kasten) return;
    setPosition(
      berechnePopoverPosition(
        symbol.getBoundingClientRect(),
        { breite: kasten.offsetWidth, hoehe: kasten.offsetHeight },
        { breite: window.innerWidth, hoehe: window.innerHeight },
      ),
    );
  }, [offen]);

  // Der Kasten steht fest im Fenster. Scrollt die Seite darunter weg, würde
  // er an der alten Stelle stehen bleiben — deshalb schließt er dabei.
  useEffect(() => {
    if (!offen) return;
    const schliessen = () => setOffen(false);
    window.addEventListener("scroll", schliessen, true);
    window.addEventListener("resize", schliessen);
    return () => {
      window.removeEventListener("scroll", schliessen, true);
      window.removeEventListener("resize", schliessen);
    };
  }, [offen]);

  if (!eintrag) return null;

  return (
    <span className="relative inline-flex">
      <button
        ref={symbolRef}
        type="button"
        className="p-0.5 rounded-full shrink-0 transition-colors"
        style={{ color: "var(--text-muted)" }}
        onMouseEnter={() => setOffen(true)}
        onMouseLeave={() => setOffen(false)}
        onClick={() => setOffen((s) => !s)}
        aria-label={`Formel zu ${label}`}
        aria-expanded={offen}
      >
        <Info size={11} />
      </button>
      {offen &&
        createPortal(
          <span
            ref={kastenRef}
            role="tooltip"
            className="fixed rounded-lg p-3 text-[11px] leading-relaxed w-72 block"
            style={{
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? "visible" : "hidden",
              zIndex: 1000,
              pointerEvents: "none",
              background: "var(--bg-card)",
              border: "1px solid var(--border)",
              color: "var(--text-secondary)",
              boxShadow: "var(--shadow-md)",
            }}
          >
            <span
              className="font-mono-data text-[10px] leading-relaxed mb-1.5 block"
              style={{ color: "var(--text-primary)" }}
            >
              {eintrag.formel}
            </span>
            <span className="block whitespace-pre-line">{eintrag.hinweis}</span>
            {/* Gleiche Farbe wie die feste Erklärung: Hier stehen die Zahlen (Anlaufziel, Listenpreis,
                Wochenkosten). In --text-muted wäre der Text mit 11 px zu blass, 3,5 : 1 im hellen und
                3,0 : 1 im dunklen Thema, nötig sind 4,5 : 1. */}
            {zusatz && (
              <span className="mt-1.5 block" style={{ color: "var(--text-secondary)" }}>
                {zusatz}
              </span>
            )}
          </span>,
          document.body,
        )}
    </span>
  );
}
